/**
 * OgPreviewTester — admin tool that shows exactly what social-network
 * link-preview bots see for a given blog post, plus deep-links to the
 * official Facebook / LinkedIn / Twitter / Slack debuggers so the
 * admin can force a re-scrape after publishing.
 *
 * Why this exists:
 *   The React SPA injects `og:image` (cover_image) via Helmet at
 *   runtime. Real bots (facebookexternalhit, LinkedInBot, Twitterbot)
 *   don't execute JavaScript — they only see what the server renders.
 *   Cloudflare Worker `/blog/{slug}` → `/api/og/social-preview/blog/{slug}`
 *   handles this in production (see /app/memory/CLOUDFLARE_BOT_SSR.md),
 *   but admins often want to verify "is my cover image actually
 *   showing on Facebook?" without leaving the editor.
 *
 * What it does:
 *   - Fetches `/api/og/social-preview/blog/{slug}` (the SSR endpoint
 *     bots are routed to) and parses out the resolved og:image URL.
 *   - Renders that image so the admin can see exactly what bots see.
 *   - Provides one-click links to each major platform's debugger,
 *     each of which doubles as a "force re-scrape" trigger.
 */
import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  ExternalLink,
  Loader2,
  Check,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const PROD_ORIGIN = 'https://clearcommunicationapp.com';

/** Extract a single <meta property="..." content="..."> value from an HTML string. */
const _readMeta = (html, prop) => {
  const re = new RegExp(
    `<meta[^>]+property=["']${prop}["'][^>]+content=["']([^"']+)["']`,
    'i',
  );
  const m = html.match(re);
  return m ? m[1] : null;
};

const OgPreviewTester = ({ slug, hasCoverImage }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvedImage, setResolvedImage] = useState(null);
  const [resolvedTitle, setResolvedTitle] = useState(null);
  const [usesCover, setUsesCover] = useState(null);
  const [error, setError] = useState(null);

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const fetchPreview = useCallback(async () => {
    if (!slug) {
      toast.error('Save the post first so it has a slug.');
      return;
    }
    setLoading(true);
    setError(null);
    setResolvedImage(null);
    setResolvedTitle(null);
    setUsesCover(null);
    try {
      // Call the production SSR endpoint via the same API origin so
      // bots and this preview see exactly the same HTML response.
      const resp = await fetch(
        `${apiBase}/api/og/social-preview/blog/${encodeURIComponent(slug)}`,
        { headers: { Accept: 'text/html' } },
      );
      if (!resp.ok) {
        if (resp.status === 404) {
          setError(
            'Post not found yet — the SSR endpoint only serves *published* posts. Publish, then try again.',
          );
        } else {
          setError(`SSR endpoint returned ${resp.status}.`);
        }
        return;
      }
      const html = await resp.text();
      const ogImage = _readMeta(html, 'og:image');
      const ogTitle = _readMeta(html, 'og:title');
      setResolvedImage(ogImage);
      setResolvedTitle(ogTitle);
      // The SSR endpoint uses the post's cover_image when present, else
      // the auto-generated card at `/api/og/blog/{slug}.png`. So if the
      // resolved og:image is NOT that fallback URL, the cover is being
      // honored — exactly what we want to confirm here.
      setUsesCover(!!ogImage && !ogImage.endsWith(`/api/og/blog/${slug}.png`));
    } catch (e) {
      setError(`Could not reach the SSR endpoint: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [slug, apiBase]);

  const liveUrl = slug ? `${PROD_ORIGIN}/blog/${slug}` : '';
  const debuggers = [
    {
      label: 'Facebook debugger',
      url: `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(liveUrl)}`,
    },
    {
      label: 'LinkedIn inspector',
      url: `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(liveUrl)}`,
    },
    {
      label: 'X / Twitter card validator',
      url: 'https://cards-dev.twitter.com/validator',
    },
    {
      label: 'Slack unfurl',
      url: 'https://api.slack.com/reference/messaging/link-unfurling',
    },
  ];

  return (
    <Card
      className="border-purple-500/30 bg-purple-500/5"
      data-testid="og-preview-tester"
    >
      <CardContent className="py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-left"
          data-testid="og-preview-tester-toggle"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <ImageIcon className="h-4 w-4 text-purple-500 shrink-0" />
          <span className="text-sm font-medium">
            Test link-preview image (what Facebook / LinkedIn / X will show)
          </span>
          {hasCoverImage ? (
            <span className="ml-auto text-[10px] text-purple-600 dark:text-purple-300 font-medium shrink-0">
              cover uploaded
            </span>
          ) : (
            <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
              auto-card
            </span>
          )}
        </button>

        {open && (
          <div className="mt-3 space-y-3 pl-1" data-testid="og-preview-tester-body">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Social bots don&apos;t run JavaScript, so they receive whatever the
              server-side renderer at{' '}
              <code className="text-[10px] bg-background px-1 rounded">/api/og/social-preview/blog/{'{slug}'}</code>{' '}
              returns. This tool fetches that endpoint and shows you the
              exact <code className="text-[10px] bg-background px-1 rounded">og:image</code>{' '}
              a bot would pick up. If a cover image is uploaded, it should
              be used directly here.
            </p>

            <Button
              size="sm"
              variant="outline"
              onClick={fetchPreview}
              disabled={loading || !slug}
              data-testid="og-preview-tester-fetch-btn"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <ImageIcon className="h-3.5 w-3.5 mr-1.5" />
              )}
              Fetch what bots see
            </Button>

            {error && (
              <div
                className="flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/30 rounded-md p-2"
                data-testid="og-preview-tester-error"
              >
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {resolvedImage && (
              <div className="space-y-2" data-testid="og-preview-tester-result">
                <div className="text-xs space-y-1">
                  <p className="flex items-center gap-1.5">
                    {usesCover ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-green-600" />
                        <span className="font-medium text-green-700 dark:text-green-300">
                          Using your uploaded cover image — bots will show this.
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                        <span className="font-medium text-amber-700 dark:text-amber-300">
                          Using the auto-generated branded card (no custom cover).
                        </span>
                      </>
                    )}
                  </p>
                  {resolvedTitle && (
                    <p className="text-muted-foreground">
                      <span className="font-medium">og:title:</span>{' '}
                      {resolvedTitle}
                    </p>
                  )}
                  <p className="text-muted-foreground break-all font-mono text-[10px]">
                    {resolvedImage}
                  </p>
                </div>
                <div className="rounded-lg overflow-hidden border bg-muted/30 max-w-2xl">
                  <div className="aspect-[1200/630] w-full">
                    <img
                      src={resolvedImage}
                      alt="Resolved og:image for bots"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      data-testid="og-preview-tester-img"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-purple-500/20 space-y-2">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Force a re-scrape (use after publishing or updating the cover)
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {debuggers.map((d) => (
                  <a
                    key={d.label}
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border bg-background hover:bg-muted transition-colors"
                    data-testid={`og-debugger-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {d.label}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Facebook and LinkedIn aggressively cache previews for ~24h.
                If the old image still shows after publishing a cover, click
                the matching debugger and hit &ldquo;Scrape again&rdquo; /
                &ldquo;Refresh&rdquo;.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OgPreviewTester;
